import { Modal, Form, Input, Button, Tabs, message, Spin } from 'antd';
import { UserOutlined, LockOutlined, PhoneOutlined, WechatOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useState, useEffect, useRef } from 'react';
import s from './index.module.css';
import {getQrCode, checkLoginStatus, getUserInfo, accountLogin} from "../../server/api";
import {MOCK_USER_INFO} from "../../config/constants";
import { writeUserInfo } from '../../utils/userInfo';
import ForgotPasswordModal from '../ForgotPasswordModal';

// Mock 开关：设置为 true 开启 mock 模式，10秒后自动登录成功
const MOCK_MODE = false;
const loginStatus = {
  0: '未登录',
  1: '扫码处理中',
  2: '登录成功',
  3: '扫码登录失败',
  4: '取消登录',
}

const LoginModal = ({ visible, onCancel, onLoginSuccess, onSwitchToRegister }) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [wechatQrCode, setWechatQrCode] = useState('');
  const [wechatLoading, setWechatLoading] = useState(false);
  const [pollingTimer, setPollingTimer] = useState(null);
  const [loginStatus, setLoginStatus] = useState('pending'); // pending, scanned, success, expired
  const [qrCodeKey, setQrCodeKey] = useState(''); // 用于轮询查询登录状态的唯一标识
  const [forgotVisible, setForgotVisible] = useState(false);

  // 在组件挂载时获取二维码
  useEffect(() => {
    if (visible === 'personal') {
      fetchWechatQrCode();
    }

    // 清理函数，在组件卸载时清除定时器
    return () => {
      if (pollingTimer) {
        clearInterval(pollingTimer);
      }
    };
  }, [visible]); // 当 visible 变化时重新执行

  // 获取微信登录二维码
  const fetchWechatQrCode = async () => {
    setWechatLoading(true);
    setLoginStatus('pending');
    try {
      const res = await getQrCode();
      const { status, data } = res;
      if (status === 200) {
        setWechatQrCode(data?.result);
        // 保存二维码的唯一标识，用于轮询查询登录状态
        // 假设后端返回的数据中包含 qrCodeKey 或类似的唯一标识
        // 如果后端没有返回，可以使用二维码URL作为标识
        const key = data?.result.capKey;
        setQrCodeKey(key);

        // Mock 模式：10秒后自动登录成功
        // 开始轮询查询登录状态（mock 模式和正常模式都调用）
        startPolling(key);

        // Mock 模式：额外设置 10 秒后自动登录成功
        if (MOCK_MODE) {
          const mockTimer = setTimeout(() => {
            setLoginStatus('success');
            handleWechatLoginSuccess({
              result: MOCK_USER_INFO,
            });
          }, 10000); // 10秒后自动登录成功

          // 保存 mock 定时器，以便在刷新二维码时清除
          setPollingTimer(mockTimer);
        }
      } else {
        message.error('获取二维码失败，请刷新重试！');
      }
    } catch (error) {
      console.error('获取二维码出错:', error);
      message.error('获取二维码失败，请检查网络后重试');
    } finally {
      setWechatLoading(false);
    }
  };

  // 轮询检查登录状态
  const startPolling = (qrKey) => {
    if (pollingTimer) {
      clearInterval(pollingTimer);
    }

    const timer = setInterval(async () => {
      try {
        // 调用 checkLoginStatus 接口查询登录状态
        const response = await checkLoginStatus({ capKey: qrKey });
        const { status, data } = response;

        if (status === 200 && data) {
          // 根据后端返回的状态码判断登录状态
          // 假设后端返回的状态码：0-待扫码，1-已扫码待确认，2-登录成功，3-二维码已过期
          const statusCode = data.result?.state;

          if (statusCode === 2) {
            // 登录成功
            clearInterval(timer);
            setLoginStatus('success');
            setPollingTimer(null);

            sessionStorage.setItem('token', data.result?.tokenStr);

            handleWechatLoginSuccess(data);
          } else if (statusCode === 1) {
            // 已扫码待确认
            setLoginStatus('scanned');
          } else if (statusCode === 3) {
            // 二维码已过期
            clearInterval(timer);
            setLoginStatus('expired');
            setPollingTimer(null);
            message.warning('二维码已过期，请刷新重试');
          }
          // statusCode === 0 时保持 pending 状态，继续轮询
        }
      } catch (error) {
        console.error('检查登录状态失败:', error);
        // 不清除定时器，继续轮询
      }
    }, 2000); // 每2秒检查一次

    setPollingTimer(timer);
  };

  // 微信登录成功处理
  const handleWechatLoginSuccess = async () => {
    try {
      const response = await getUserInfo();
      const resultData = response?.data?.result;
      if (!resultData) {
        message.error('获取用户信息失败');
        return;
      }

      const userInfo = {
        ...resultData,
        state: resultData.state,
        token: resultData.token,
        roleCode: resultData.roleCode,
        userType: resultData.userType,
        userName: resultData.pubUserName,
        id: resultData.userId,
        avatar: resultData.avatar,
      };

      writeUserInfo(userInfo);

      message.success('登录成功！');

      if (onLoginSuccess) {
        onLoginSuccess(userInfo);
      }

      onCancel();
    } catch (error) {
      console.error('登录处理失败:', error);
      message.error('登录失败');
    }
  };

  // 清理轮询定时器
  useEffect(() => {
    return () => {
      if (pollingTimer) {
        clearInterval(pollingTimer);
      }
    };
  }, [pollingTimer]);

  // 监听弹窗显示状态
  useEffect(() => {
    if (visible) {
      setLoginStatus('pending');
      setWechatQrCode('');
      setQrCodeKey('');
    } else {
      if (pollingTimer) {
        clearInterval(pollingTimer);
        setPollingTimer(null);
      }
    }
  }, [visible]);

  // 刷新二维码
  const refreshQrCode = () => {
    // 清除之前的定时器（包括轮询定时器和mock定时器）
    if (pollingTimer) {
      clearInterval(pollingTimer);
      clearTimeout(pollingTimer);
      setPollingTimer(null);
    }
    setLoginStatus('pending');
    setWechatQrCode('');
    setQrCodeKey('');
    fetchWechatQrCode();
  };

  const handleLogin = async (values) => {
    setLoading(true);
    try {
      // 模拟登录API调用

      if (MOCK_MODE) {
        // 模拟API延迟
        await new Promise(resolve => setTimeout(resolve, 1000));

        // 模拟登录成功
        message.success('登录成功！');

        // 存储用户信息到sessionStorage
        const userInfo = MOCK_USER_INFO;
        sessionStorage.setItem('userInfo', JSON.stringify(userInfo));

        if (onLoginSuccess) {
          onLoginSuccess(userInfo);
        }

        onCancel();
      } else {
        let res = {};

        res = await accountLogin({
          username: values.username,
          password: values.password,
          type: visible === 'personal' ? 1 : 2,
        });

        const { data: data1 } = res;

        setLoginStatus('success');
        sessionStorage.setItem('token', data1.result?.tokenStr);

        const response = await getUserInfo();
        const { status, data } = response;

        const resultData = data.result;

        let userInfo = {
          ...resultData,
          state: resultData.state, // 状态（1：正常；2：待审批/驳回）
          token: resultData.token,
          roleCode: resultData.roleCode,
          userType: resultData.userType, // 登录类型：1：个人；2：机构
          userName: resultData.pubUserName,
          id: resultData.userId,
          avatar: resultData.avatar,
        };

        if (visible === 'personal') {
          // if (userInfo.state != 1) {
          //   message.success('登录失败，错误的用户状态！');
          //   return;
          // }

          const personalInfo = resultData?.pubUserInfo || {};

          userInfo = {
            ...userInfo,
            // id: personalInfo?.id,
            // userName: personalInfo?.wxName,
            phoneNo: personalInfo?.phoneNo,
            // avatar: personalInfo?.avatar,
            lastLoginTime: personalInfo?.lastLoginTime,
            email: '',
            registerLogList: [],
          }
        } else if (visible === 'organization') {
          const orgInfo = resultData?.checkOrgInfo || {};

          userInfo = {
            ...userInfo,
            // id: orgInfo?.id,
            // userName: orgInfo?.name,
            phoneNo: orgInfo?.contactTel,
            // avatar: orgInfo?.checkLogoUrl,
            lastLoginTime: orgInfo?.lastLoginTime || '',
            email: orgInfo?.orgEmail,
            orgProfile: orgInfo,
            registerLogList: resultData?.registerLogList || [],
          }
        }

        sessionStorage.setItem('userInfo', JSON.stringify(userInfo));

        message.success('登录成功！');

        if (onLoginSuccess) {
          onLoginSuccess(userInfo);
        }

        onCancel();
      }



    } catch (error) {
      message.error('登录失败，请检查用户名和密码');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async () => {
    const phone = form.getFieldValue('phone');
    if (!phone) {
      message.warning('请先输入手机号');
      return;
    }

    // 模拟发送验证码
    message.success('验证码已发送到您的手机');
  };

  const clearRefresh = () => {
    // 清除之前的定时器（包括轮询定时器和mock定时器）
    if (pollingTimer) {
      clearInterval(pollingTimer);
      clearTimeout(pollingTimer);
      setPollingTimer(null);
    }
    setLoginStatus('pending');
    setWechatQrCode('');
    setQrCodeKey('');
  };

  return (
    <Modal
      title={visible === 'personal' ? '个人用户登录' : '机构用户登录'}
      open={visible}
      onCancel={() => {
        onCancel();
        form.resetFields();
        setWechatQrCode('');
        setLoginStatus('pending');
      }}
      footer={null}
      width={400}
      className={s.loginModal}
      destroyOnClose={true}
    >
      <Tabs
        defaultActiveKey={visible === 'personal' ? 'qrCode' : 'account'}
        centered
        items={[
          {
            key: 'qrCode',
            disabled: visible === 'organization',
            label: <span><WechatOutlined />小程序扫码</span>,
            children: (
              <div className={s.wechatLoginContainer}>
                {wechatLoading ? (
                    <div className={s.wechatLoadingContainer}>
                      <Spin size="large" />
                      <p>正在生成二维码...</p>
                    </div>
                ) : loginStatus === 'success' ? (
                    <div className={s.wechatSuccessContainer}>
                      <WechatOutlined className={s.successIcon} />
                      <p>登录成功！</p>
                    </div>
                ) : wechatQrCode ? (
                    <>
                      <div className={s.qrCodeContainer}>
                        <img src={wechatQrCode.qrImage} alt="登录二维码" className={s.qrCodeImage} />
                        {loginStatus === 'scanned' && (
                          <div className={s.qrCodeScannedOverlay}>
                            <WechatOutlined className={s.scannedIcon} />
                            <span>已扫码，请在手机上确认</span>
                          </div>
                        )}
                        {loginStatus === 'expired' && (
                          <div className={s.qrCodeExpiredOverlay}>
                            <ExclamationCircleOutlined className={s.expiredIcon} />
                            <span>二维码已过期</span>
                          </div>
                        )}
                      </div>
                      <p className={s.wechatTip}>
                        {loginStatus === 'pending' && '请使用「庆质通」小程序扫码登录'}
                        {loginStatus === 'scanned' && '请在手机上确认登录'}
                        {loginStatus === 'expired' && '二维码已过期，请刷新'}
                      </p>
                      <Button
                          type="link"
                          onClick={refreshQrCode}
                          className={s.refreshBtn}
                      >
                        刷新二维码
                      </Button>
                    </>
                ) : (
                    <div className={s.wechatLoadingContainer}>
                      <Spin size="large" />
                      <p>正在加载...</p>
                    </div>
                )}
              </div>
            )
          },
          {
            key: 'account',
            label: '账号密码',
            children: (
              <Form
                form={form}
                name="accountLogin"
                onFinish={handleLogin}
                autoComplete="off"
                layout="vertical"
                
              >
                <Form.Item
                  name="username"
                  rules={[{ required: true, message: visible === 'personal' ? '请输入用户名/邮箱/手机号' : '请输入统一社会信用代码/邮箱/手机号' }]}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder= {visible === 'personal' ? '用户名/邮箱/手机号' : '统一社会信用代码/邮箱/手机号'}
                    size="large"
                    autoComplete="new-password"
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  rules={[{ required: true, message: '请输入密码' }]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="密码"
                    size="large"
                    autoComplete="new-password"
                  />
                </Form.Item>

                <Form.Item>
                  {visible === 'organization' && (
                    <div className={s.forgotRow}>
                      <Button
                        type="link"
                        className={s.forgotBtn}
                        onClick={() => setForgotVisible(true)}
                      >
                        忘记密码？
                      </Button>
                    </div>
                  )}
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    size="large"
                    block
                    className={s.loginButton}
                  >
                    登录
                  </Button>
                </Form.Item>
              </Form>
            )
          },
          // {
          //   key: 'sms',
          //   label: '短信登录',
          //   disabled: true,
          //   children: (
          //     <Form
          //         name="smsLogin"
          //         onFinish={handleLogin}
          //         autoComplete="off"
          //         layout="vertical"
          //     >
          //       <Form.Item
          //           name="phone"
          //           rules={[
          //             { required: true, message: '请输入手机号' },
          //             { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' }
          //           ]}
          //       >
          //         <Input
          //             prefix={<PhoneOutlined />}
          //             placeholder="手机号"
          //             size="large"
          //         />
          //       </Form.Item>
          //
          //       <Form.Item
          //           name="code"
          //           rules={[{ required: true, message: '请输入验证码' }]}
          //       >
          //         <div className={s.codeInputGroup}>
          //           <Input
          //               placeholder="验证码"
          //               size="large"
          //               style={{ flex: 1 }}
          //           />
          //           <Button
          //               onClick={handleSendCode}
          //               size="large"
          //               className={s.sendCodeBtn}
          //           >
          //             发送验证码
          //           </Button>
          //         </div>
          //       </Form.Item>
          //
          //       <Form.Item>
          //         <Button
          //             type="primary"
          //             htmlType="submit"
          //             loading={loading}
          //             size="large"
          //             block
          //             className={s.loginButton}
          //         >
          //           登录
          //         </Button>
          //       </Form.Item>
          //     </Form>
          //   )
          // }
        ]}
        onChange={(key) => {
          if (key === 'qrCode' && !wechatQrCode) {
            fetchWechatQrCode();
          } else {
            clearRefresh();
          }
        }}
      />

      <div className={s.loginFooter}>
        <span>还没有账号？</span>
        <Button
          type="link"
          onClick={() => {
            if (onSwitchToRegister) {
              onSwitchToRegister(visible);
            }
          }}
        >
          立即注册
        </Button>
      </div>

      <ForgotPasswordModal
        open={forgotVisible}
        onCancel={() => setForgotVisible(false)}
      />
    </Modal>
  );
};

export default LoginModal;
